import router from '@/router';
import { Toast } from '@hb/coffee';
import { RouterV2Enum } from '@/router/router-names';
import { VerifyTypeEnum } from '@/api/verify-credit/types';
import PopupView from '@/components/popup-view';
import { preloadVerifyCreditIdentity } from '@/shared/tools/ocr/utils/loader';
import { getUserInfoProcessConfig } from '@/api/verify-credit';
import { VueRouter } from 'vue-router/types/router';
import URLUtils from '@/shared/utils/URLUtils';
import {
  jumpToCreditProcessingPage, jumpToCreditResultPage,
  jumpToHomeV2Page, jumpToApplyProductPage, backToPreviousPage,
  jumpToApplyResultPage, jumpToCashActivityResultPage,
  shopMallCallback,
  shopMallCallback2,
} from './sucess-verify-action';
import { resolveCashActivityQualification } from './unfinished-verify-action';
import {
  OpenVerifyPageMode,
  StartVerifyCreditOptions,
  VerifySceneEnum,
  ResolveVerifyStageEnum,
  PresentationConfig,
  VerifyQueryParamsTypeEnum,
} from './types';
import { jumpToRedirectHelloApp } from '../utils/jumpTo';
import { CASH_ACTIVITY_CONFIG, POCKET_MONEY_FIRST_INFO_CONFIG } from './presentation-config';

/**
 * 资料验证流程成功后，各场景回调方法配置
 * 成功检验后，按照如下优先级，取级别最高的方法执行：
 * 1. 调用传参自定义， successCb方法
 * 2. 此处配置场景回调方法， RESOLVE_VERIFY_FOR_SCENES[scene]
 * 3. 通用兜底方法， $router.back
 */
export const RESOLVE_VERIFY_FOR_SCENES:
Partial<Record<VerifySceneEnum, ((
  query: Dictionary<string>,
  stage?: ResolveVerifyStageEnum,
) => void) | null>> = {
  /** 极简流程 */
  [VerifySceneEnum.CREDIT_PRE_VERIFY]: jumpToCreditProcessingPage,
  [VerifySceneEnum.CREDIT_PRE_VERIFY_OCR_AFTER]: jumpToCreditProcessingPage,
  [VerifySceneEnum.CREDIT_POST_SUPPLEMENT]: jumpToCreditResultPage,
  [VerifySceneEnum.CREDIT_MIDDLE_ONLY_OCR]: jumpToCreditResultPage,
  [VerifySceneEnum.LOAN_UNION_SUPPLEMENT]: jumpToApplyProductPage,
  /** 预授信流程 */
  [VerifySceneEnum.PRE_CREDIT_FIRST_INFO]: jumpToApplyProductPage,
  [VerifySceneEnum.PRE_CREDIT_ADD_INFO]: jumpToApplyResultPage,
  /** 个人信息资料补充 */
  [VerifySceneEnum.CREDIT_SUPPLY_PERSONAL]: jumpToCreditResultPage,
  /** 不区分是否极简的“授信”流程 */
  [VerifySceneEnum.CREDIT_MIX_SUPPLEMENT]: jumpToCreditProcessingPage,
  /** 特殊定制化的信息填写场景 */
  [VerifySceneEnum.UPDATE_EXPIRES_IDENTITY]: backToPreviousPage,
  [VerifySceneEnum.MARKET_PRE_SUPPLEMENT]: jumpToRedirectHelloApp,
  [VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY]: jumpToCreditProcessingPage,
  [VerifySceneEnum.PERSONAL_PROFILE_MODIFY]: backToPreviousPage,
  [VerifySceneEnum.PERSONAL_CONTACT_MODIFY]: backToPreviousPage,
  [VerifySceneEnum.PERSONAL_IDENTITY_MODIFY]: function callback(this: VueRouter) {
    /** toast提示 */
    Toast('保存成功！');
    /** 回跳上一页 */
    backToPreviousPage.call(this);
  },
  [VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT]: jumpToCreditProcessingPage,
  [VerifySceneEnum.PERSONAL_NAME_MODIFY]: backToPreviousPage,
  /** 现金活动场景 */
  [VerifySceneEnum.CREDIT_CASH_ACTIVITY]: jumpToCashActivityResultPage,
  /** 零用钱流程场景 */
  [VerifySceneEnum.POCKET_MONEY_FIRST_INFO]: jumpToApplyProductPage,
  /** 分期商城场景 */
  [VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING]: shopMallCallback,
  /** 授信失败实验组二分期商城场景 */
  [VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING_2]: shopMallCallback2,
};

/** 未完成验证流程时，各场景回调方法配置 */
export const RESOLVE_UNFINISHED_VERIFY_FOR_SCENES:
Partial<Record<VerifySceneEnum, ((
  query: Dictionary<string>,
) => void) | null>> = {
  [VerifySceneEnum.CREDIT_CASH_ACTIVITY]: resolveCashActivityQualification,
};

/** 资料验证流程失败后，各场景回调方法，默认无操作 */
export const FAIL_VERIFY_FOR_SCENES = {
  [VerifySceneEnum.CREDIT_POST_SUPPLEMENT]: jumpToHomeV2Page,
};

/** 跳转验证页的配置缓存 */
export const JUMP_VERIFY_OPTIONS_CACHE: Record<string, StartVerifyCreditOptions | undefined> = {};

/** 核验场景值转换(前端scene >> 服务端scene) */
export function verifySceneMapping(outerScene: string): VerifyTypeEnum | null {
  switch (outerScene) {
    case VerifySceneEnum.CREDIT_PRE_VERIFY:
    case VerifySceneEnum.CREDIT_MIX_SUPPLEMENT:
      return VerifyTypeEnum.FIRST_INFO;
    case VerifySceneEnum.CREDIT_PRE_VERIFY_OCR_AFTER:
      return VerifyTypeEnum.FIRST_INFO_OCR_AFTER;
    case VerifySceneEnum.CREDIT_POST_SUPPLEMENT:
    case VerifySceneEnum.LOAN_UNION_SUPPLEMENT:
    case VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING:
    case VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING_2:
      return VerifyTypeEnum.ADD_INFO;
    case VerifySceneEnum.PRE_CREDIT_FIRST_INFO:
    case VerifySceneEnum.POCKET_MONEY_FIRST_INFO:
      return VerifyTypeEnum.PRE_CREDIT_FIRST_INFO;
    case VerifySceneEnum.PRE_CREDIT_ADD_INFO:
      return VerifyTypeEnum.PRE_CREDIT_ADD_INFO;
    case VerifySceneEnum.CREDIT_CASH_ACTIVITY:
    case VerifySceneEnum.CREDIT_MIDDLE_ONLY_OCR:
      return VerifyTypeEnum.ONLY_ID_CARD;
    case VerifySceneEnum.TWO_ELEMENTS_VERIFY:
      return VerifyTypeEnum.TWO_ELEMENTS;
    default:
      return null;
  }
}

/** 跳转到验证流程 */
export default function jumpToVerifyCredit(
  scene: VerifySceneEnum,
  options?: StartVerifyCreditOptions,
): void {
  const {
    successCb, queryParams = {}, openMode = OpenVerifyPageMode.ROUTER_JUMP,
    commodityIds, creditId, useReplace,
  } = options || {};
  const jumpId = String(Date.now());

  if (!scene || (!RESOLVE_VERIFY_FOR_SCENES[scene] && !successCb)) {
    throw new Error('The scene must be a VerifySceneEnum or a access successCb');
  }

  /** 设置缓存 */
  JUMP_VERIFY_OPTIONS_CACHE[jumpId] = options;
  const componentProps = {
    ...queryParams,
    openMode,
    scene,
    jumpId,
    commodityIds: commodityIds && commodityIds.length ? commodityIds.join(',') : '',
    creditId: String(creditId || ''),
  };

  if (openMode === OpenVerifyPageMode.POPUP_SHOW) {
    import('@/views_v2/verify-credit').then((component) => {
      preloadVerifyCreditIdentity();
      PopupView.show(component.default, componentProps);
    });
  } else {
    router[useReplace ? 'replace' : 'push']({
      path: RouterV2Enum.verifyCredit,
      query: componentProps,
    });
  }
}

/**
 * 检验指定场景的资料填写是否已经完成，如，预授信流程-前置ocr是否已上传
 * @param scene 资料填写-场景值
 * @returns
 */
export async function checkVerifyCreditCompleteByScene(scene: VerifySceneEnum): Promise<boolean> {
  try {
    const serverVerifyType = verifySceneMapping(scene);
    if (!serverVerifyType) {
      throw new Error('The scene must be a VerifySceneEnum');
    }

    const { infoComplete } = await getUserInfoProcessConfig(serverVerifyType);
    return infoComplete;
  } catch (error) {
    return false;
  }
}

/**
 * 检查绑卡前所需要的资料填写是否已填写完成
 * 若完成，返回 Promise.resolve
 * 若未完成，跳到资料页
 * @returns Promise<void>
 */
export async function checkBindCardNeedVerify(
  commodityIds?: string[],
  customQueryParams: Record<string, unknown> = {},
): Promise<void> {
  try {
    const isComplete = await checkVerifyCreditCompleteByScene(VerifySceneEnum.TWO_ELEMENTS_VERIFY);

    /** 若所需资料填写完成，则直接返回 */
    if (isComplete) {
      return Promise.resolve();
    }

    /** 若资料不完整，跳转到资料填写页 */
    return new Promise((resolve, reject) => {
      jumpToVerifyCredit(
        VerifySceneEnum.TWO_ELEMENTS_VERIFY,
        {
          commodityIds,
          queryParams: {
            type: VerifyQueryParamsTypeEnum.CardBind,
            closeBackConfirmDialog: true,
            ...customQueryParams,
          },
          successCb: () => resolve(),
          failCb: () => reject(),
        },
      );
    });
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * 获取认证页标题
 */
export function getVerifyCreditPageTitle(verifyType: VerifyTypeEnum): string {
  switch (verifyType) {
    case VerifyTypeEnum.ONLY_ID_CARD:
      return '身份认证';
    default:
      return '';
  }
}

/**
 * 获取认证页导航栏配置
 */
export function getVerifyCreditPageNavBarCustomProps(
  customProps: Record<string, unknown>,
  verifyType: VerifyTypeEnum,
): Record<string, unknown> {
  switch (verifyType) {
    case VerifyTypeEnum.ONLY_ID_CARD:
      return {
        useImage: false,
        showCenterTitle: true,
        textAlign: 'center',
        ...customProps,
      };
    default:
      return {
        useImage: false,
        showCenterTitle: true,
        ...customProps,
      };
  }
}

/** 获取身份验证场景 */
const getVerifyCreditSence = (): VerifySceneEnum | unknown => {
  const urlQuery = URLUtils.getQueryMap();
  return urlQuery.scene;
};

/**
 * 身份认证页表现层配置映射
 * 数据包括：主题样式、文案模块等，用于控制身份认证页展示
 */
const PRESENTATION_CONFIG_MAP: Partial<Record<VerifySceneEnum, PresentationConfig>> = {
  [VerifySceneEnum.CREDIT_CASH_ACTIVITY]: CASH_ACTIVITY_CONFIG,
  [VerifySceneEnum.POCKET_MONEY_FIRST_INFO]: POCKET_MONEY_FIRST_INFO_CONFIG,
};

/**
 * 获取表现层配置
 * 用于身份认证页根据场景获取表现层配置，如主题样式、文案模块等
 */
export const getPresentationConfig = (): PresentationConfig => {
  const scene = getVerifyCreditSence();
  return PRESENTATION_CONFIG_MAP[scene as VerifySceneEnum] || {};
};
